if ( typeof ModalBox !== 'function' ) {

  class ModalBox extends HTMLElement {

    constructor() {

      window.inertElems = document.querySelectorAll('[data-js-inert]');

      super();
      this.o = {
        ...{
          show: 10,
          showOnPageOffset: -1,
          frequency: "day",
          enabled: true,
          showOnce: true,
          closeByKey: true,
          disableScroll: true,
          enableClose: false,
          type: false,
          blockTabNavigation: false,
          openedModalBodyClass: "modal-opened",
        },
        ...JSON.parse(this.dataset.options),
      };

      if (this.o.type === 'cookies') {
        // The existing theme banner remains the only consent UI and writer.
        this.o.enabled = false;
        const choiceButtons = this.querySelectorAll('[data-js-cookies-accept], [data-js-cookies-decline]');
        const notice = document.createElement('p');
        notice.setAttribute('data-cookie-preferences-notice', '');
        notice.setAttribute('role', 'status');
        notice.setAttribute('aria-live', 'polite');
        notice.style.cssText = 'margin:1rem;padding:0;';
        notice.hidden = true;
        this.appendChild(notice);
        if (!this.querySelector('[data-js-first-focus]') && choiceButtons[0]) {
          choiceButtons[0].setAttribute('data-js-first-focus', '');
        }

        const privacy = () => window.Shopify && window.Shopify.customerPrivacy;
        const processing = () => {
          const api = privacy();
          const result = {};
          [['analytics', 'analyticsProcessingAllowed'], ['marketing', 'marketingAllowed']].forEach(([key, method]) => {
            try {
              const value = api && typeof api[method] === 'function' ? api[method]() : null;
              result[key] = typeof value === 'boolean' ? value : null;
            } catch (_) { result[key] = null; }
          });
          return result;
        };
        const snapshot = () => {
          const api = privacy();
          let saved = {};
          try { saved = api.currentVisitorConsent() || {}; } catch (_) {}
          const result = {};
          ['analytics', 'marketing', 'preferences'].forEach(key => {
            result[key] = ['yes', 'no', ''].includes(saved[key]) ? saved[key] : 'unavailable';
          });
          const effective = {};
          [['analytics', 'analyticsProcessingAllowed'], ['marketing', 'marketingAllowed'], ['preferences', 'preferencesProcessingAllowed']].forEach(([key, method]) => {
            try { effective[key] = typeof api[method] === 'function' ? api[method]() === true : null; }
            catch (_) { effective[key] = null; }
          });
          // Nonpersonal state only, exposed for exact DOM-based QA readback.
          this.setAttribute('data-cookie-consent-choice', JSON.stringify(result));
          this.setAttribute('data-cookie-consent-effective', JSON.stringify(effective));
          return result;
        };
        const status = (state, message = '') => {
          this.setAttribute('data-cookie-consent-status', state);
          notice.textContent = message;
          notice.hidden = !message;
        };
        const busy = value => {
          this._cookieConsentBusy = value;
          this.setAttribute('aria-busy', value ? 'true' : 'false');
          choiceButtons.forEach(button => { button.disabled = value; });
        };
        let reloadRequested = false;
        const reloadCookieDocument = reason => {
          if (reloadRequested) return;
          reloadRequested = true;
          busy(true);
          this.setAttribute('data-cookie-consent-reload', reason);
          status('refreshing', reason === 'withdrawal'
            ? 'Preferences saved. Refreshing this page…'
            : 'Refreshing cookie preferences…');
          // A new document discards already-loaded native SDK listeners and queues.
          // Never unlock or replay the old SDK after the visitor withdraws consent.
          window.location.reload();
        };
        window.addEventListener('pageshow', event => {
          if (!event.persisted) return;
          // Stop restored-document senders before the fresh document takes over.
          event.stopImmediatePropagation();
          // A restored API object may carry stale in-memory consent. Reload to
          // reinitialize Shopify from its persisted choice without reading cookies.
          reloadRequested = false;
          reloadCookieDocument('bfcache');
        }, { capture: true });
        const ensurePrivacy = callback => {
          const ready = () => {
            const api = privacy();
            return api && typeof api.setTrackingConsent === 'function' &&
              typeof api.currentVisitorConsent === 'function';
          };
          if (ready()) { callback(); return; }
          try {
            if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
              callback(new Error('privacy-unavailable')); return;
            }
            window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
              callback(error || (!ready() ? new Error('privacy-unavailable') : null));
            });
          } catch (error) { callback(error); }
        };
        const fail = () => {
          busy(false);
          snapshot();
          status('error', 'We could not save your cookie preferences. Please try again.');
        };
        const saveChoice = (allowed, event) => {
          event.preventDefault();
          // Cookie buttons must never follow the generic immediate-close path.
          event.stopImmediatePropagation();
          if (this._cookieConsentBusy) return;
          busy(true);
          status('saving', 'Saving your cookie preferences…');
          let completed = false;
          let uncertain = false;
          let beforeProcessing;
          const timeout = setTimeout(() => {
            if (completed) return;
            uncertain = true;
            status('unconfirmed', 'We could not confirm your cookie preferences. Please reload and try again.');
            const retry = document.createElement('button');
            retry.type = 'button';
            retry.textContent = 'Reload and try again';
            retry.style.cssText = 'display:block;margin-top:.5rem;text-decoration:underline;';
            retry.addEventListener('click', () => window.location.reload());
            notice.appendChild(retry);
            // Keep choices disabled until the unknown write finishes or reloads.
          }, 8000);
          const finish = success => {
            if (completed) return;
            completed = true;
            clearTimeout(timeout);
            if (!success) { fail(); return; }
            try { localStorage.setItem('krown-cookie-banner', 'true'); } catch (_) {}
            const afterProcessing = processing();
            const withdrew = !allowed && beforeProcessing &&
              !(beforeProcessing.analytics === false && beforeProcessing.marketing === false) &&
              afterProcessing.analytics === false && afterProcessing.marketing === false;
            if (withdrew) {
              reloadCookieDocument('withdrawal');
              return;
            }
            busy(false);
            status('saved');
            this.hide(false);
          };
          ensurePrivacy(error => {
            if (completed) return;
            if (error) { finish(false); return; }
            // A timed-out API load has not submitted a choice. Let the visitor retry.
            if (uncertain) { finish(false); return; }
            try {
              // Read the current native API immediately before this user choice.
              beforeProcessing = processing();
              privacy().setTrackingConsent({ analytics: allowed, marketing: allowed, preferences: allowed }, () => {
                const saved = snapshot();
                const expected = allowed ? 'yes' : 'no';
                finish(saved.analytics === expected && saved.marketing === expected && saved.preferences === expected);
              });
            } catch (_) { finish(false); }
          });
        };
        choiceButtons.forEach(button => {
          button.addEventListener('click', event => saveChoice(button.hasAttribute('data-js-cookies-accept'), event));
        });
        this.openCookiePreferences = opener => {
          if (opener) window.lastFocusedElm = opener;
          snapshot();
          if (!this._cookieConsentBusy) status('ready');
          this.show();
        };
        document.addEventListener('visitorConsentCollected', snapshot);
        ensurePrivacy(error => {
          snapshot();
          if (!this._cookieConsentBusy) {
            if (error) status('unavailable', 'Cookie preferences are temporarily unavailable. Please try again.');
            else status('ready');
          }
          let dismissed = false;
          try { dismissed = localStorage.getItem('krown-cookie-banner') === 'true'; } catch (_) {}
          if (!dismissed) this.show();
        });
      }

      this._scrollTriggered = false; // To track if scroll condition has been met

      if ( this.o.enabled ) {
        this._modalKey = `modal-${document.location.hostname}-${this.id}`;
        this._modalStorage = !localStorage.getItem(this._modalKey)
          ? "empty"
          : JSON.parse(localStorage.getItem(this._modalKey));
        if (this.querySelector("[data-content]")) {
          this._modalText = this.querySelector("[data-content]").textContent;
        }

        const timeNow = new Date().getTime();
        const inBetween = Math.round(
          (timeNow - this._modalStorage['shown']) / 1000
        );

        let showModal = false;

        if (
          this._modalStorage == 'empty' ||
          (this.o.frequency == 'day' && inBetween > 86400) ||
          (this.o.frequency == 'week' && inBetween > 604800) ||
          (this.o.frequency == 'month' && inBetween > 2419200) ||
          this._modalStorage['content'] != this._modalText
        ) {
          showModal = true;
        }

        if ( showModal ) {

          if ( this.o.type == 'exit_intent_popup' ) {

            let lastMouseY = 0; 
            let topOffset = 20; 
    
            document.addEventListener('mousemove', e => {
              const currentMouseY = e.clientY;
              if (currentMouseY < lastMouseY && currentMouseY < topOffset && ! Shopify.designMode) { 
                this.show();
              }
              lastMouseY = currentMouseY;
            });

          } else if ( this.o.showOnPageOffset > 0 ) {
            this._onScroll = () => {
              const scrollPercent =
                (window.scrollY /
                  (document.documentElement.scrollHeight -
                    window.innerHeight)) *
                100;
              if (
                scrollPercent >= this.o.showOnPageOffset &&
                !this._scrollTriggered
              ) {
                this._scrollTriggered = true;
                setTimeout(() => {
                  this.show();
                }, parseInt(this.o.show * 1000));
                window.removeEventListener('scroll', this._onScroll);
              }
            };
            window.addEventListener('scroll', this._onScroll, {
              passive: true,
            });

          } else {
            setTimeout(() => {
              this.show();
            }, parseInt(this.o.show * 1000));
          }

        }
        this.querySelectorAll(this.o.type === 'cookies' ? '[data-js-close]:not([data-js-cookies-accept]):not([data-js-cookies-decline])' : '[data-js-close]').forEach(elm =>
          elm.addEventListener('click', () => {
            this.hide(this.o.showOnce);
          })
        );
      } else {
        this.querySelectorAll(this.o.type === 'cookies' ? '[data-js-close]:not([data-js-cookies-accept]):not([data-js-cookies-decline])' : '[data-js-close]').forEach(elm =>
          elm.addEventListener('click', () => {
            this.hide(this.o.showOnce);
          })
        );
      }

      if ( this.o.enableClose == true ) {
        this.querySelectorAll(this.o.type === 'cookies' ? '[data-js-close]:not([data-js-cookies-accept]):not([data-js-cookies-decline])' : '[data-js-close]').forEach(elm =>
          elm.addEventListener('click', () => {
            this.hide(this.o.showOnce);
          })
        );
      }

      if ( this.o.closeByKey ) {
        document.addEventListener('keydown', e => {
          if (e.keyCode == 27) {
            if (this.classList.contains('active')) {
              this.hide(this.o.showOnce);
            }
          }
        });
      }
    }

    show(customContent = false) {

      if (this.o.type === 'cookies') {
        clearTimeout(this._cookieHideTimer);
        clearTimeout(this._cookieReturnFocusTimer);
        clearTimeout(this._cookieShowActivationTimer);
        clearTimeout(this._cookieShowFocusTimer);
      }

      if (customContent && document.querySelector(customContent)) {
        const content = document.querySelector(customContent).innerHTML;
        const modalCommon = document.getElementById('modal-common');
        modalCommon.innerHTML = content;
        if (this.o.enableClose == true) {
          modalCommon.querySelectorAll('[data-js-close]').forEach(elm =>
            elm.addEventListener('click', () => {
              this.hide(this.o.showOnce);
            })
          );
        }
      }

      this.setAttribute('style', '');
      const showActivationTimer = setTimeout(() => {
        this.classList.add('active');
        if (this.o.disableScroll) {
          document.body.classList.add(this.o.openedModalBodyClass);
        }
        if (this.o.blockTabNavigation) {
          window.inertElems.forEach(elm =>
            elm.setAttribute('inert', '')
          );
        }
      }, 10);
      if (this.o.type === 'cookies') this._cookieShowActivationTimer = showActivationTimer;

      const showFocusTimer = setTimeout(() => {
        if (this.querySelector('[data-js-first-focus]')) {
          this.querySelector('[data-js-first-focus]').focus();
        }
      }, 250);
      if (this.o.type === 'cookies') this._cookieShowFocusTimer = showFocusTimer;
      
    }

    hide(remember = false) {

      if (this.o.type === 'cookies') {
        if (this._cookieConsentBusy) return;
        clearTimeout(this._cookieShowActivationTimer);
        clearTimeout(this._cookieShowFocusTimer);
        remember = false;
      }

      this.classList.remove('active');
      document.body.classList.remove(this.o.openedModalBodyClass);
      const hideTimer = setTimeout(() => {
        this.style.display = 'none';
      }, 500);
      if (this.o.type === 'cookies') this._cookieHideTimer = hideTimer;
      if (remember && !Shopify.designMode) {
        localStorage.setItem(
          this._modalKey,
          JSON.stringify({
            shown: new Date().getTime(),
            content: this._modalText,
          })
        );
      }

      window.inertElems.forEach(elm =>
        elm.removeAttribute('inert')
      );
      if (window.lastFocusedElm) {
        const focusTimer = setTimeout(() => {
          if (window.lastFocusedElm) window.lastFocusedElm.focus();
          window.lastFocusedElm = null;
        }, 100);
        if (this.o.type === 'cookies') this._cookieReturnFocusTimer = focusTimer;
      }

    }
  }

  if ( typeof customElements.get('modal-box') == 'undefined' ) {

    // Clone modal elements and move to end of body IF append-top-body
    const originalElements = document.querySelectorAll('modal-box');
    originalElements.forEach((originalElement) => {
      const dataOptions = JSON.parse(originalElement.dataset.options);
      if (dataOptions.appendToBody === true) {
        document.body.appendChild(originalElement);
      }
    });

    customElements.define('modal-box', ModalBox);

  }

  // Shopify events

  document.addEventListener('shopify:section:select', e => {
    if (e.target.classList.contains('mount-popup')) {
      e.target.querySelector('modal-box').style.display = 'block';
      e.target.querySelector('modal-box').show();
    }
  });
  document.addEventListener('shopify:block:select', e => {
    if (e.target.hasAttribute('data-modal-box')) {
      e.target.style.display = 'block';
      e.target.show();
    }
  });
  document.addEventListener('shopify:block:deselect', e => {
    if (e.target.hasAttribute('data-modal-box')) {
      e.target.hide();
    }
  });
  document.addEventListener('shopify:section:deselect', e => {
    if (e.target.classList.contains('mount-popup')) {
      e.target.querySelector('modal-box').hide();
    }
  });
	
}
// One delegated listener survives footer section redraws without duplicate saves.
if (!window.__dosisCookiePreferencesBound) {
  window.__dosisCookiePreferencesBound = true;
  document.addEventListener('click', event => {
    const button = event.target && event.target.closest && event.target.closest('[data-dosis-cookie-preferences]');
    if (!button) return;
    event.preventDefault();
    const modal = document.getElementById(button.getAttribute('aria-controls'));
    const feedback = button.parentElement.querySelector('[data-cookie-preferences-feedback]');
    if (modal && modal.o && modal.o.type === 'cookies' && typeof modal.openCookiePreferences === 'function') {
      if (feedback) feedback.textContent = '';
      modal.openCookiePreferences(button);
    } else if (feedback) {
      feedback.textContent = 'Cookie preferences are unavailable. Please refresh and try again.';
    }
  });
}
